import { useState } from 'react';

import { plural } from '@lingui/core/macro';
import { clsx } from 'clsx';
import Skeleton from 'react-loading-skeleton';

import 'react-loading-skeleton/dist/skeleton.css';
import styles from './BankStatementsOpenBanking.module.css';
import ManualUploadAccordion from './ManualUploadAccordion';
import { useGetContext } from '../../../../api/lendingApi/lendingApiQueries';
import { useIsInDEOpenBankingCopyTest } from '../../../../hooks/useIsInDEOpenBankingCopyTest';
import { useLocale } from '../../../../hooks/useLocale';
import { useSignupParams } from '../../../../hooks/useSignupParams';
import { useSplitTestContext } from '../../../../hooks/useSplitTestContext';
import {
  TOfferDataRequirement,
  TTrueLayerAlertStatus,
} from '../../../../types';
import Required from '../../DocumentRequirement/Required';
import OpenBanking from '../../OpenBanking/OpenBanking';
import { getLinkedBankAccountDetails } from '../../OpenBanking/trueLayer.helpers';
import TrueLayerAlertBox from '../../OpenBanking/TrueLayerAlertBox/TrueLayerAlertBox';
import TrueLayerLinkedAccount from '../../OpenBanking/TrueLayerLinkedAccount/TrueLayerLinkedAccount';
import TrueLayerShareableLink from '../../OpenBanking/TrueLayerShareableLink/TrueLayerShareableLink';

type TProps = {
  quantifier: number;
  status?: TOfferDataRequirement['actions'][number]['status'];
  isSubmitted: boolean;
  resolutionMethods: TOfferDataRequirement['actions'][number]['resolution_methods'];
};

const BankStatementOpenBanking = ({
  quantifier,
  status,
  isSubmitted,
  resolutionMethods,
}: TProps) => {
  const [trueLayerAlertStatus, setTrueLayerAlertStatus] =
    useState<TTrueLayerAlertStatus>(null);

  const { context } = useGetContext();
  const { isUK, isDE } = useLocale();
  const { isDirectCustomer } = useSignupParams();
  const { hasOpenBankingShareableLinkEnabled } = useSplitTestContext();
  const { isLoading: isSplitTestLoading } = useIsInDEOpenBankingCopyTest();

  const renderTitle = () => {
    if (isDE) {
      return plural(quantifier, {
        one: 'Business bank statements for the last # month',
        other: 'Business bank statements for the last # months',
      });
    }

    return `Business bank statements for the last ${
      quantifier > 1 ? `${quantifier} months` : '1 month'
    } (from today)`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        {/* id has space instead of hyphen to match other dynamic ids used for scrolling to requirements */}
        <h4
          className={clsx(styles.title, styles.titleWithSubtitle)}
          id="document-requirement-bank statement"
        >
          {renderTitle()}
          {isSubmitted && (status === 'required' || status === 'error') && (
            <Required />
          )}
        </h4>
      </div>

      {isSplitTestLoading ? (
        <Skeleton
          height={198}
          baseColor="var(--color-structure-90)"
          highlightColor="var(--color-structure-80)"
        />
      ) : (
        <>
          <OpenBanking setAlertStatus={setTrueLayerAlertStatus} />
          {isUK && isDirectCustomer && hasOpenBankingShareableLinkEnabled && (
            <TrueLayerShareableLink />
          )}
          <ManualUploadAccordion
            title={renderTitle()}
            status={status}
            isSubmitted={isSubmitted}
          />
        </>
      )}
      {trueLayerAlertStatus && (
        <TrueLayerAlertBox status={trueLayerAlertStatus} />
      )}
      <TrueLayerLinkedAccount
        linkedBankAccount={getLinkedBankAccountDetails(
          resolutionMethods,
          context,
        )}
      />
    </div>
  );
};

export default BankStatementOpenBanking;
